/**
 * 通常入札タブの ⋯ メニュー項目の「有効 / 無効 (+ 無効の理由)」を決める純関数。
 *
 * 無効理由は項目の `title` (tooltip) に出す (design/CLAUDE.md「⋯ 項目の無効理由は
 * title に載せる」)。 理由の分岐を UI から切り離しておくことで、 ワークフローケース
 * 経由の読み取り専用など「ページ経由では組みにくい状態」も含めて全分岐を F1 で
 * 固定できる。 文言 (i18n key) の対応は呼び出し側 (NormalBidTab) が持つ。
 */

/** CSV ダウンロード (機能詳細8): 絞り込み後の表示中の札が出力対象。 */
export type CsvDownloadState = 'active' | 'empty'

/**
 * 入札を全削除 (機能詳細7 / AC-066 / AC-082): 入札一覧に表示中の DRAFT の札のみが対象。
 * `noVisibleDraft` は下書きはあるが表示中の下書きが 0 件の状態 (絞り込みで隠れている)。
 */
export type ClearAllState =
  | 'active'
  | 'empty'
  | 'noDraft'
  | 'noVisibleDraft'
  | 'readonly'

export const resolveCsvDownloadState = (
  visibleBidCount: number,
): CsvDownloadState => (visibleBidCount === 0 ? 'empty' : 'active')

/**
 * 理由は「読み取り専用 → 札 0 件 → DRAFT request 無し → 表示中の下書き無し」の順に返す。
 * 読み取り専用は他の理由を直しても解消しないため最優先で伝える。
 */
export const resolveClearAllState = ({
  bidCount,
  hasDraftRequest,
  hasVisibleDraft,
  isReadonly,
}: {
  bidCount: number
  hasDraftRequest: boolean
  hasVisibleDraft: boolean
  isReadonly: boolean
}): ClearAllState => {
  if (isReadonly) return 'readonly'
  if (bidCount === 0) return 'empty'
  if (!hasDraftRequest) return 'noDraft'
  if (!hasVisibleDraft) return 'noVisibleDraft'
  return 'active'
}

/**
 * 受渡日 X に札を新規作成できるか。 入札追加 (個別作成・一括作成)・コマクリック
 * (NormalBidTab の canCreateBid) と失効した札の複製 (AC-BSP-011) が同じ述語を使い、
 * 作成導線ごとに条件がずれないようにする。
 *
 *   - isBeforeDeadline: X の入札締切前か (AC-079 / AC-064)
 *   - isReadonly: ワークフローケースが読み取り専用か (workflow-back-bar-readonly AC-15)
 *   - approvalFlowUnavailable: 承認フロー設定が取得中・取得失敗のままか (AC-BSP-015)
 *   - isFocusedDraftUnavailable: 承認前の札確認 (AC-BSP-094) などで入札一覧を 1 つの
 *     申請セットに絞り込み、 追加先の下書きが画面に無いか (保存は
 *     SpotFocusedRequestUnavailableError で止まるので、 押す前に導線を止める)
 */
export const canCreateSpotBid = ({
  isBeforeDeadline,
  isReadonly,
  approvalFlowUnavailable,
  isFocusedDraftUnavailable,
}: {
  isBeforeDeadline: boolean
  isReadonly: boolean
  approvalFlowUnavailable: boolean
  isFocusedDraftUnavailable: boolean
}): boolean =>
  isBeforeDeadline &&
  !isReadonly &&
  !approvalFlowUnavailable &&
  !isFocusedDraftUnavailable
